import * as React from 'react';
import { SafeText } from '../../shared/components';
import { EditorWrapper } from '../../shared/components/Editor/EditorWrapper';
import { useIsEditor } from '../../shared/hooks';

export type HtmlRendererProps = {
  fields: any;
};

const HtmlRenderer: React.FC<HtmlRendererProps> = ({ fields = {} }) => {
  const htmlField = fields['HTML'];
  const isEditor = useIsEditor();
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Inject raw HTML directly into DOM, bypassing React entirely.
  // React 19 cannot handle raw HTML with <script>, <form>, <style> etc.
  // Using innerHTML + manual script execution is the only reliable approach.
  React.useEffect(() => {
    if (!containerRef.current || !htmlField?.value || isEditor) return;

    const html = htmlField.value;

    // Extract scripts before inserting HTML
    const scriptRegex = /<script([\S\s]*?)>([\S\s]*?)<\/script>/gi;
    const scripts: { src?: string; content?: string }[] = [];
    let match;
    while ((match = scriptRegex.exec(html)) !== null) {
      const attrs = match[1];
      const content = match[2];
      const srcMatch = attrs.match(/src=["']([^"']+)["']/);
      scripts.push(srcMatch ? { src: srcMatch[1] } : { content });
    }

    // Insert HTML without scripts (they don't execute via innerHTML)
    containerRef.current.innerHTML = html.replace(scriptRegex, '');

    // Load and execute scripts sequentially to preserve order.
    // External scripts (src) must finish loading before the next script runs,
    // since later scripts may depend on earlier ones (e.g. jQuery → plugin).
    let cancelled = false;
    const loadScriptsInOrder = async () => {
      for (const { src, content } of scripts) {
        if (cancelled || !containerRef.current) break;

        await new Promise<void>((resolve) => {
          const script = document.createElement('script');
          if (src) {
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => resolve(); // continue even if one fails
          } else if (content) {
            script.textContent = content;
          }
          containerRef.current!.appendChild(script);
          // Inline scripts execute synchronously on append, resolve immediately
          if (!src) resolve();
        });
      }
    };
    loadScriptsInOrder();

    return () => {
      cancelled = true;
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [htmlField?.value, isEditor]);

  // In editor mode, show stripped HTML via SafeText for editing support
  if (isEditor) {
    const renderedHtmlField = <SafeText field={htmlField} encode={false} editable={false} />;
    const strippedHtml = htmlField.value
      .replace(/<script([\S\s]*?)>([\S\s]*?)<\/script>/gi, '')
      .replace(/<style([\S\s]*?)>([\S\s]*?)<\/style>/gi, '');

    return strippedHtml !== '' ? (
      <EditorWrapper.MinHeight>{renderedHtmlField}</EditorWrapper.MinHeight>
    ) : (
      <EditorWrapper.EmptyOutline>
        HTML Renderer
        {renderedHtmlField}
      </EditorWrapper.EmptyOutline>
    );
  }

  // Public site: empty div, filled via useEffect (bypasses React completely)
  return <div ref={containerRef} />;
};

export default HtmlRenderer;
